import React, { useState } from "react";
import apiService from "../services/apiService";

const SECTION_TITLE = { fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 };

function slideSummary(slide) {
  const cfg = slide.chartConfig || {};
  const parts = [cfg.chartType || "doughnut", cfg.bannerScope === "targets" ? "targets" : "main"];
  if (cfg.sigDisplay && cfg.sigDisplay !== "none") parts.push(cfg.sigDisplay);
  return parts.join(" · ");
}

export default function ChartDeckManager({ open, onClose, projectId, chartDecks, onChangeChartDecks }) {
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [exportingDeck, setExportingDeck] = useState(null);
  const [exportWarnings, setExportWarnings] = useState(null);

  if (!open) return null;

  const decks = chartDecks || [];

  const addDeck = () => {
    const name = `Chart Deck ${decks.length + 1}`;
    onChangeChartDecks([...decks, { name, slides: [], enabled: true }]);
    setExpandedIdx(decks.length);
  };

  const removeDeck = (i) => {
    onChangeChartDecks(decks.filter((_, idx) => idx !== i));
    setExpandedIdx(null);
  };

  const updateDeck = (i, field, value) => {
    onChangeChartDecks(decks.map((d, idx) => (idx === i ? { ...d, [field]: value } : d)));
  };

  const removeSlide = (deckIdx, slideIdx) => {
    const deck = decks[deckIdx];
    const nextSlides = (deck.slides || []).filter((_, idx) => idx !== slideIdx);
    updateDeck(deckIdx, "slides", nextSlides);
  };

  const moveSlide = (deckIdx, slideIdx, dir) => {
    const deck = decks[deckIdx];
    const slides = [...(deck.slides || [])];
    const target = slideIdx + dir;
    if (target < 0 || target >= slides.length) return;
    [slides[slideIdx], slides[target]] = [slides[target], slides[slideIdx]];
    updateDeck(deckIdx, "slides", slides);
  };

  const exportDeck = async (deck) => {
    if (!projectId || !deck.name) return;
    setExportingDeck(deck.name);
    setExportWarnings(null);
    try {
      const url = `${apiService.baseURL}/export_chart_deck/${projectId}?name=${encodeURIComponent(deck.name)}`;
      const response = await fetch(url);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `Export failed (${response.status})`);
      }
      const warnHeader = response.headers.get("X-Chart-Deck-Warning-Detail");
      if (warnHeader) {
        try { setExportWarnings(JSON.parse(warnHeader)); } catch (_e) { /* ignore */ }
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${deck.name}.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      alert(`Export failed: ${e.message}`);
    } finally {
      setExportingDeck(null);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.65)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "#0f172a", border: "1px solid #1e2d45",
        borderRadius: 12, width: 560, maxWidth: "95vw",
        maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        padding: "24px 28px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0" }}>Chart Decks</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
              Build named PowerPoint decks from charts configured in question Properties.
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: "#64748b", fontSize: 18, cursor: "pointer", padding: "2px 6px" }}
          >×</button>
        </div>

        <div style={SECTION_TITLE}>Decks</div>

        {decks.length === 0 && (
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 12 }}>
            No chart decks yet. Configure a chart in a SingleResponse question's Properties panel
            and use "Add to Deck" to start one.
          </div>
        )}

        {decks.map((deck, i) => (
          <div key={deck.name + i} style={{
            background: "#0c1928", border: "1px solid #1e3a5f",
            borderRadius: 8, marginBottom: 8, overflow: "hidden",
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", cursor: "pointer",
            }} onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}>
              <div
                onClick={(e) => { e.stopPropagation(); updateDeck(i, "enabled", !deck.enabled); }}
                style={{
                  width: 28, height: 16, borderRadius: 8, flexShrink: 0,
                  background: deck.enabled ? "#2563eb" : "#334155",
                  position: "relative", cursor: "pointer", transition: "background 0.2s",
                }}
              >
                <div style={{
                  position: "absolute", top: 2, left: deck.enabled ? 14 : 2,
                  width: 12, height: 12, borderRadius: "50%", background: "#fff", transition: "left 0.2s",
                }} />
              </div>
              <input
                value={deck.name}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => updateDeck(i, "name", e.target.value)}
                style={{
                  flex: 1, background: "transparent", border: "none",
                  color: deck.enabled ? "#e2e8f0" : "#64748b", fontSize: 12, fontWeight: 600, outline: "none",
                }}
              />
              <span style={{ fontSize: 10, color: "#60a5fa", background: "#1e3a5f", padding: "1px 6px", borderRadius: 10 }}>
                {(deck.slides || []).length} slide{(deck.slides || []).length === 1 ? "" : "s"}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); exportDeck(deck); }}
                disabled={exportingDeck === deck.name || (deck.slides || []).length === 0}
                title="Export this deck as an editable .pptx"
                style={{
                  fontSize: 10, padding: "3px 8px", borderRadius: 6,
                  border: "1px solid #2563eb", background: "#1e3a5f", color: "#93c5fd",
                  cursor: (deck.slides || []).length === 0 ? "not-allowed" : "pointer",
                  opacity: (deck.slides || []).length === 0 ? 0.5 : 1,
                }}
              >
                {exportingDeck === deck.name ? "Exporting…" : "Export PPTX"}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); removeDeck(i); }}
                style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, padding: "0 2px" }}
                title="Remove this deck"
              >×</button>
              <span style={{ color: "#64748b", fontSize: 10 }}>{expandedIdx === i ? "▲" : "▼"}</span>
            </div>

            {expandedIdx === i && (
              <div style={{ padding: "0 12px 12px" }}>
                {(deck.slides || []).length === 0 && (
                  <div style={{ fontSize: 10, color: "#475569" }}>No slides added yet.</div>
                )}
                {(deck.slides || []).map((slide, si) => (
                  <div key={slide.id || si} style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "5px 8px",
                    background: "#0f1b2d", borderRadius: 6, marginBottom: 4,
                  }}>
                    <span style={{ fontSize: 10, color: "#64748b", width: 18 }}>{si + 1}.</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {slide.qtitleSnapshot || slide.datakey}
                      </div>
                      <div style={{ fontSize: 9, color: "#64748b" }}>{slideSummary(slide)}</div>
                    </div>
                    <button
                      onClick={() => moveSlide(i, si, -1)}
                      disabled={si === 0}
                      style={{ background: "none", border: "none", color: si === 0 ? "#334155" : "#8a9bb0", cursor: si === 0 ? "not-allowed" : "pointer", fontSize: 12 }}
                      title="Move up"
                    >↑</button>
                    <button
                      onClick={() => moveSlide(i, si, 1)}
                      disabled={si === (deck.slides || []).length - 1}
                      style={{ background: "none", border: "none", color: si === (deck.slides || []).length - 1 ? "#334155" : "#8a9bb0", cursor: si === (deck.slides || []).length - 1 ? "not-allowed" : "pointer", fontSize: 12 }}
                      title="Move down"
                    >↓</button>
                    <button
                      onClick={() => removeSlide(i, si)}
                      style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 13 }}
                      title="Remove this slide"
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <button
          onClick={addDeck}
          style={{
            width: "100%", background: "#1e3a5f", border: "1px dashed #334155",
            color: "#60a5fa", borderRadius: 7, padding: "7px 0", fontSize: 12, cursor: "pointer", marginBottom: 10,
          }}
        >
          + New Chart Deck
        </button>

        {exportWarnings && exportWarnings.length > 0 && (
          <div style={{
            marginTop: 10, padding: "8px 10px", background: "#2a1a0c",
            border: "1px solid #92400e", borderRadius: 7, fontSize: 10, color: "#fbbf24",
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Export completed with {exportWarnings.length} warning(s):</div>
            {exportWarnings.map((w, i) => <div key={i}>• {w}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}
